import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTrip } from '../contexts/TripContext';
import { daysService } from '@trip-planner-monorepo/days';
import { tripService } from '../services/tripService';

export function useDays(tripId) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();

  // Fetch days
  const fetchDays = useCallback(async () => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      const fetchedDays = await daysService.fetchDays(effectiveUserId, tripId);
      setDays(fetchedDays);
      return fetchedDays;
    } catch (err) {
      setError('Failed to load days. Please try again.');
      console.error('Error fetching days:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, effectiveUserId]);

  // Create days
  const createDays = useCallback(async (newDays) => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      if (!daysService.validateDaysLimit(days.length, newDays.length)) {
        throw new Error('Maximum trip duration is 60 days');
      }

      for (const day of newDays) {
        await daysService.createDay(effectiveUserId, tripId, day);
      }

      const updatedDays = await fetchDays();

      // Update trip dates
      if (updatedDays && updatedDays.length > 0) {
        const dateArray = updatedDays
          .map(day => day.date)
          .filter(date => date !== null);

        if (dateArray.length > 0) {
          const sortedDates = [...dateArray].sort();
          await tripService.updateTripDates(
            effectiveUserId,
            tripId,
            sortedDates[0],
            sortedDates[sortedDates.length - 1]
          );
        }
      }

      return updatedDays;
    } catch (err) {
      setError(err.message || 'Failed to create days. Please try again.');
      console.error('Error creating days:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, days.length, fetchDays, effectiveUserId]);

  // Update day
  const updateDay = useCallback(async (dayId, dayData) => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      await daysService.updateDay(effectiveUserId, tripId, dayId, dayData);
      const updatedDays = await fetchDays();

      // Update trip dates
      if (updatedDays && updatedDays.length > 0) {
        const dateArray = updatedDays
          .map(day => day.date)
          .filter(date => date !== null);

        if (dateArray.length > 0) {
          const sortedDates = [...dateArray].sort();
          await tripService.updateTripDates(
            effectiveUserId,
            tripId,
            sortedDates[0],
            sortedDates[sortedDates.length - 1]
          );
        }
      }

      return updatedDays;
    } catch (err) {
      setError('Failed to update day. Please try again.');
      console.error('Error updating day:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, fetchDays, effectiveUserId]);

  // Delete day
  const deleteDay = useCallback(async (dayId) => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      await daysService.deleteDay(effectiveUserId, tripId, dayId);
      const updatedDays = await fetchDays();

      // Update trip dates
      if (updatedDays && updatedDays.length > 0) {
        const dateArray = updatedDays
          .map(day => day.date)
          .filter(date => date !== null);

        if (dateArray.length > 0) {
          const sortedDates = [...dateArray].sort();
          await tripService.updateTripDates(
            effectiveUserId,
            tripId,
            sortedDates[0],
            sortedDates[sortedDates.length - 1]
          );
        } else {
          // If no days have dates, reset trip dates
          await tripService.updateTripDates(
            effectiveUserId,
            tripId,
            null,
            null
          );
        }
      }

      return updatedDays;
    } catch (err) {
      setError('Failed to delete day. Please try again.');
      console.error('Error deleting day:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, fetchDays, effectiveUserId]);

  // Reorder days
  const reorderDays = useCallback(async (reorderedDays) => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      const updatedDays = await daysService.reorderDays(
        effectiveUserId,
        tripId,
        reorderedDays
      );
      setDays(updatedDays);
      return updatedDays;
    } catch (err) {
      setError('Failed to reorder days. Please try again.');
      console.error('Error reordering days:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, effectiveUserId]);

  // Initial fetch
  useEffect(() => {
    fetchDays();
  }, [fetchDays]);

  return {
    days,
    loading,
    error,
    createDays,
    updateDay,
    deleteDay,
    reorderDays,
    fetchDays,
  };
}